import React from 'react';
import classNames from 'classnames';

import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { createSlug, stringify } from '../../../util/urlHelpers';
import { useIntl } from '../../../util/reactIntl';
import {
  defaultCaregiverSummary,
  getOptionLabel,
  parseServiceRadiusLabel,
} from '../../../util/caregiverSearch';
import { getActiveListingServices, getDisplayServiceForListing } from '../../../util/listingServices';

import { AvatarLarge, NamedLink } from '../../../components';

import css from './CaregiverListingCard.module.css';

const { Money } = sdkTypes;
const MAX_ADDITIONAL_SERVICES = 3;
const MAX_EXPERIENCE_CHIPS = 2;
const MAX_BADGES = 5;

/**
 * Map listing entity data into caregiver card display props.
 *
 * @param {Object} listing Listing entity.
 * @param {Object} intl Intl instance.
 * @returns {Object} Card-friendly listing data.
 */
export const getCaregiverCardData = (listing, intl, searchParams = {}) => {
  const { title = '', publicData = {}, price } = listing?.attributes || {};
  const author = listing?.author;
  const profile = author?.attributes?.profile || {};
  const providerName = profile.displayName || title;
  const activeServices = getActiveListingServices(publicData);
  const { service: displayService, isSearchMatched, labelPrefix } = getDisplayServiceForListing(
    listing,
    searchParams
  );
  const servicePriceLabel =
    displayService && Number.isInteger(displayService?.priceAmount) && price?.currency
      ? formatMoney(intl, new Money(displayService.priceAmount, price.currency))
      : price
      ? formatMoney(intl, price)
      : null;
  const additionalServices = activeServices
    .filter(service => service?.key && service.key !== displayService?.key)
    .slice(0, MAX_ADDITIONAL_SERVICES)
    .map(service => service.title || getOptionLabel('services', service.key));
  const experienceTags = (Array.isArray(publicData.experience) ? publicData.experience : [])
    .slice(0, MAX_EXPERIENCE_CHIPS)
    .map(value => getOptionLabel('experience', value));
  const certifications = Array.isArray(publicData.cert_training) ? publicData.cert_training : [];
  const transportation = Array.isArray(publicData.transportation_req)
    ? publicData.transportation_req
    : [];
  const badgeLabels = [...certifications, ...transportation]
    .map(value =>
      certifications.includes(value)
        ? getOptionLabel('cert_training', value)
        : getOptionLabel('transportation_req', value)
    )
    .slice(0, MAX_BADGES);
  const languages = Array.isArray(publicData.languages) ? publicData.languages : [];
  const languageLabels = languages.map(value => getOptionLabel('languages', value));
  const languageText = [languageLabels.join(', '), publicData.other_languages]
    .filter(Boolean)
    .join(languageLabels.length > 0 && publicData.other_languages ? ', ' : '');
  const summary = displayService?.description || publicData.service_notes || defaultCaregiverSummary;
  const experienceLevelLabel = publicData.experience_level
    ? getOptionLabel('experience_level', publicData.experience_level)
    : null;
  const radiusLabel = parseServiceRadiusLabel(publicData.serviceRadius);
  const category = displayService?.category || publicData.category || null;
  const primaryService = displayService?.key || null;

  return {
    id: listing?.id?.uuid,
    slug: createSlug(title),
    title,
    providerName,
    summary,
    category,
    primaryService,
    additionalServices,
    experienceLevelLabel,
    experienceTags,
    badgeLabels,
    languageText,
    radiusLabel,
    priceLabel: servicePriceLabel,
    author,
    displayService,
    isSearchMatched,
    labelPrefix,
  };
};

/**
 * Caregiver-focused listing card used on the redesigned search page.
 *
 * @param {Object} props Component props.
 * @param {Object} props.listing Listing entity.
 * @param {string} [props.className] Optional root class name.
 * @returns {JSX.Element}
 */
const CaregiverListingCard = props => {
  const intl = useIntl();
  const { listing, className, searchParams = {} } = props;
  const card = getCaregiverCardData(listing, intl, searchParams);
  const categoryLabel = card.category
    ? getOptionLabel('category', card.category)
    : intl.formatMessage({ id: 'CaregiverListingCard.defaultType' });
  const primaryServiceLabel = card.displayService?.title || (
    card.primaryService ? getOptionLabel('services', card.primaryService) : null
  );
  const linkSearch = card.displayService?.key
    ? `?${stringify({ service: card.displayService.key })}`
    : null;
  const angelMark = (
    <svg
      className={css.ctaAngel}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      role="presentation"
    >
      <path
        className={css.ctaAngelWing}
        d="M5.2 14.6c-2.1-1-3.2-2.8-3.2-4.9 2.7-.2 4.9 1 6.2 3.3-.8.2-1.6.7-2.4 1.6-.2.2-.4.2-.6 0z"
      />
      <path
        className={css.ctaAngelWing}
        d="M18.8 14.6c2.1-1 3.2-2.8 3.2-4.9-2.7-.2-4.9 1-6.2 3.3.8.2 1.6.7 2.4 1.6.2.2.4.2.6 0z"
      />
      <circle className={css.ctaAngelFace} cx="12" cy="10.1" r="2.2" />
      <path className={css.ctaAngelHalo} d="M8.8 6.5c.9-.9 2-1.3 3.2-1.3s2.3.4 3.2 1.3" />
      <path
        className={css.ctaAngelBody}
        d="M12 12.7c2 0 3.7 1.7 3.7 3.7V18H8.3v-1.6c0-2 1.7-3.7 3.7-3.7z"
      />
    </svg>
  );

  return (
    <NamedLink
      className={classNames(css.root, className)}
      name="ListingPage"
      params={{ id: card.id, slug: card.slug }}
      to={linkSearch ? { search: linkSearch } : undefined}
    >
      <div className={css.photoColumn}>
        <AvatarLarge className={css.avatar} user={card.author} disableProfileLink />
      </div>

      <div className={css.content}>
        <div className={css.headerRow}>
          <div className={css.primaryInfo}>
            <p className={css.eyebrow}>{categoryLabel}</p>
            <h3 className={css.providerName}>{card.providerName}</h3>
            {primaryServiceLabel ? (
              <p className={css.primaryService}>{primaryServiceLabel}</p>
            ) : null}
          </div>
          {card.priceLabel ? (
            <div className={css.pricePanel}>
              <p className={css.priceLabel}>
                {intl.formatMessage({ id: card.labelPrefix })}
              </p>
              <p className={css.price}>{card.priceLabel}</p>
            </div>
          ) : null}
        </div>

        <div className={css.metaRow}>
          {card.experienceLevelLabel ? (
            <span className={css.experienceText}>
              {intl.formatMessage(
                { id: 'CaregiverListingCard.experienceLabel' },
                { experience: card.experienceLevelLabel }
              )}
            </span>
          ) : null}
          {card.radiusLabel ? <span className={css.infoChip}>{card.radiusLabel}</span> : null}
          {card.languageText ? (
            <span className={css.infoChip}>
              {intl.formatMessage(
                {
                  id: 'CaregiverListingCard.languagesLabel',
                  defaultMessage: 'Languages: {languages}',
                },
                { languages: card.languageText }
              )}
            </span>
          ) : null}
        </div>

        {card.additionalServices.length > 0 ? (
          <div className={css.serviceChipsBlock}>
            <p className={css.serviceChipsLabel}>
              {intl.formatMessage({ id: 'CaregiverListingCard.alsoOffers' })}
            </p>
            <div className={css.serviceChips}>
            {card.additionalServices.map(label => (
              <span key={label} className={css.serviceChip}>
                {label}
              </span>
            ))}
            </div>
          </div>
        ) : null}

        {card.experienceTags.length > 0 ? (
          <div className={css.secondaryChips}>
            {card.experienceTags.map(label => (
              <span key={label} className={css.secondaryChip}>
                {label}
              </span>
            ))}
          </div>
        ) : null}

        <p className={css.shortBlurb}>{card.summary}</p>

        <div className={css.trustSection}>
          <p className={css.trustLabel}>
            {intl.formatMessage({ id: 'CaregiverListingCard.trustHeading' })}
          </p>
          <div className={css.badges}>
            {card.badgeLabels.length > 0 ? (
              card.badgeLabels.map(label => (
                <span key={label} className={css.badge}>
                  {label}
                </span>
              ))
            ) : (
              <span className={css.badgePlaceholder}>
                {intl.formatMessage({
                  id: 'CaregiverListingCard.badgePlaceholder',
                  defaultMessage: 'Profile includes additional caregiving details',
                })}
              </span>
            )}
          </div>
        </div>

        <div className={css.footerRow}>
          <span className={css.cta}>
            {angelMark}
            <span>{intl.formatMessage({ id: 'CaregiverListingCard.cta' })}</span>
          </span>
        </div>
      </div>
    </NamedLink>
  );
};

export default CaregiverListingCard;
